import React from 'react';
import { PromiseState } from 'react-refetch';

import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import LeaveList from './LeaveList';
import Modal from 'components/utility/Modal/Modal';
import EditList from './EditList';
import DeleteList from './DeleteList';
import CuratorShareList from './ShareList/CuratorShareList';
import FollowerShareList from './ShareList/FollowerShareList';
import { TTableHeader } from 'components/CustomizeView/CustomizeView';

import { isInMyLists } from 'redux/reducers/myLists';
import { addToMyLists } from 'redux/reducers/myLists';
import isUserLastCuratorForList from 'components/customlists/utils/isUserLastCuratorForList';
import { isUserCuratorForList } from 'components/customlists/utils/isUserCuratorForList';

import { ICustomList, ICustomListData } from './AddToList/shared';
import { TModalContent } from './CustomListPageHeader';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';
interface ICustomListModal {
  customList: ICustomList;
  customListMetadata: PromiseState<ICustomList>;
  customListInvitationData: PromiseState<any>;
  deleteCustomEntityList: (listId: string, next: () => void) => any;
  fetchCustomListInvitation: ({ listId }: { listId: string }) => any;
  leaveCustomEntityList: (
    listId: string,
    userId: string,
    close: () => void
  ) => void;
  hideModal: () => void;
  modalContent: TModalContent;
  putCustomEntityList: (list: ICustomListData, close: () => void) => void;
  saveToApi: (headers: TTableHeader[]) => void;
  userId: string;
}

const CustomListModal: React.FC<ICustomListModal> = (props) => {
  const {
    customList,
    customListMetadata,
    customListInvitationData,
    deleteCustomEntityList,
    fetchCustomListInvitation,
    hideModal,
    leaveCustomEntityList,
    modalContent,
    putCustomEntityList,
    userId,
  } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const listHasAlreadyBeenAdded = useSelector(isInMyLists(customList?.id));
  const isCurator = isUserCuratorForList({ customList, userId });
  const isLastCurator = isCurator
    ? isUserLastCuratorForList({ customList, userId })
    : false;
  const leaveAndFollowList = (): void => {
    if (!listHasAlreadyBeenAdded) {
      // Bit of a dirty cast to circumvent redux's nebulous typing
      const result = dispatch(
        addToMyLists(customList?.id)
      ) as any as Promise<any>;
      result.then(() => {
        leaveCustomEntityList(customList?.id, userId as string, hideModal);
      });
    } else {
      leaveCustomEntityList(customList?.id, userId as string, hideModal);
    }
  };

  const shareListRefreshCuratorLink = (): void =>
    fetchCustomListInvitation({
      listId: (customList as ICustomList).id,
    });

  return (
    <>
      <Modal hide={hideModal} canClickOutside={true} isNarrow={true}>
        {modalContent === 'EDIT_LIST' && (
          <EditList
            close={hideModal}
            list={customList}
            saveList={(list: ICustomListData): void => {
              putCustomEntityList(list, hideModal);
            }}
            saveButtonLabel="Update list"
            title="List information"
            pending={customListMetadata?.pending}
            error={customListMetadata?.reason}
          />
        )}
        {modalContent === 'DELETE_LIST' && (
          <DeleteList
            close={hideModal}
            pending={customListMetadata?.pending}
            deleteAction={(): void => {
              deleteCustomEntityList(customList?.id, () => {
                history.push('/');
              });
              logToAmplitude('User has deleted a list');
            }}
          />
        )}
        {modalContent === 'LEAVE_LIST' && userId && (
          <LeaveList
            close={hideModal}
            pending={customListMetadata?.pending}
            isLastCurator={isLastCurator}
            deleteAction={(): void => {
              deleteCustomEntityList(customList?.id, () => {
                history.push('/');
              });
            }}
            leaveAction={(): void => {
              leaveAndFollowList();
            }}
          />
        )}
        {modalContent === 'SHARE_LIST' && isCurator && (
          <CuratorShareList
            error={customListInvitationData.reason}
            close={hideModal}
            refreshAction={shareListRefreshCuratorLink}
            pending={customListInvitationData.pending}
            invitationData={customListInvitationData}
          />
        )}
        {modalContent === 'SHARE_LIST' && !isCurator && (
          <FollowerShareList close={hideModal} />
        )}
      </Modal>
    </>
  );
};

export default CustomListModal;
